import { getFileExtension, getFileTypeIcon } from '../../../utils/documentType';
import {
    DownloadUploadedFileIcon,
    RemoveUploadedFileIcon,
} from '../icons/icons';

import './SusyFileBox.css';

interface ISusyFileBoxProps {
    fileName: string;
    description?: string;
    onDownloadFile?: () => void;
    onRemoveFile?: () => void;
    dataTestId?: string;
    classes?: string;
    stretchToContainer?: boolean;
    showFileTypeIcon?: boolean;
}

const SusyFileBox = ({
    fileName,
    description,
    onDownloadFile,
    onRemoveFile,
    dataTestId,
    classes,
    stretchToContainer = true,
    showFileTypeIcon = true,
}: ISusyFileBoxProps) => {
    const renderDescription = () => {
        if (!description) {
            return null;
        }

        return (
            <div
                className='susy-uploaded-file-item-description file-uploader-item-muted-text'
                data-testid={buildDescriptionTestId(getDataTestId())}
            >
                {description}
            </div>
        );
    };

    const isDownloadable = onDownloadFile !== undefined;
    const isRemovable = onRemoveFile !== undefined;

    const getDataTestId = () => {
        return dataTestId ?? 'susy-file-box';
    };

    const getWrapperClassName = () => {
        const res = ['susy-uploaded-file-item', 'd-flex'];
        if (isDownloadable) {
            res.push('downloadable');
        }
        if (stretchToContainer) {
            res.push('stretch-to-container');
        }
        if (classes) {
            res.push(classes);
        }

        return res.join(' ');
    };

    return (
        <div className={getWrapperClassName()} data-testid={getDataTestId()}>
            {showFileTypeIcon && (
                <div className='susy-uploaded-file-item-img d-flex align-items-center mr-3'>
                    <img
                        className='file-item-img'
                        src={getFileTypeIcon(getFileExtension(fileName))}
                        data-testid={buildIconTestId(getDataTestId())}
                    />
                </div>
            )}

            <div
                className='susy-uploaded-file-item-metadata d-flex align-items-center'
                onClick={isDownloadable ? onDownloadFile : undefined}
            >
                <div className='d-flex'>
                    <div>
                        <div
                            className='d-flex file-uploader-item-text'
                            data-testid={buildFilenameTestId(getDataTestId())}
                        >
                            <div>{fileName}</div>
                        </div>

                        {renderDescription()}
                    </div>
                </div>
            </div>

            <div className='susy-uploaded-file-item-buttons d-flex ml-3'>
                {isDownloadable && (
                    <div
                        onClick={() => onDownloadFile()}
                        className='susy-uploaded-file-item-download-btn cursor-pointer'
                        data-testid={buildDownloadButtonTestId(getDataTestId())}
                    >
                        <DownloadUploadedFileIcon />
                    </div>
                )}

                {isRemovable && (
                    <div
                        onClick={() => onRemoveFile()}
                        className='susy-uploaded-file-item-remove-btn cursor-pointer'
                        data-testid={buildRemoveButtonTestId(getDataTestId())}
                    >
                        <RemoveUploadedFileIcon />
                    </div>
                )}
            </div>
        </div>
    );
};

export const buildFilenameTestId = (testId: string) => {
    return `${testId}-name`;
};

export const buildDescriptionTestId = (testId: string) => {
    return `${testId}-description`;
};

export const buildIconTestId = (testId: string) => {
    return `${testId}-icon`;
};

export const buildDownloadButtonTestId = (testId: string) => {
    return `${testId}-download-button`;
};

export const buildRemoveButtonTestId = (testId: string) => {
    return `${testId}-remove-button`;
};

export default SusyFileBox;
